import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Camera, Upload, Trash2, Loader2, Info, ArrowLeftRight, ChevronDown, Share2, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { buildTransformationImage } from './shareTransformation';
import { BeforeAfterSlider } from './BeforeAfterSlider';

const ANGLES = [
  { key: 'front', fr: 'Face', en: 'Front' },
  { key: 'side', fr: 'Profil', en: 'Side' },
  { key: 'back', fr: 'Dos', en: 'Back' },
];
const angleLabel = (k, en) => { const a = ANGLES.find((x) => x.key === k); return a ? (en ? a.en : a.fr) : k; };

const TIPS = {
  fr: [
    'Même lumière et même heure (idéalement le matin, à jeun).',
    'Même distance et même cadrage à chaque photo.',
    'Tenue moulante ou minimale pour bien voir les contours.',
    'Prenez 3 angles : face, profil et dos, bras détendus le long du corps.',
    'Fond neutre et dégagé, téléphone à la verticale, à hauteur du nombril.',
  ],
  en: [
    'Same lighting and time of day (ideally morning, fasted).',
    'Same distance and framing for every photo.',
    'Tight or minimal clothing to clearly see your shape.',
    'Take 3 angles: front, side and back, arms relaxed along the body.',
    'Neutral, clear background, phone held vertically at navel height.',
  ],
};

const fileToResizedBase64 = (file, max = 1080, q = 0.82) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(url);
    resolve(c.toDataURL('image/jpeg', q));
  };
  img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
  img.src = url;
});

export const ProgressPhotos = ({ api, authFetch, en }) => {
  const [photos, setPhotos] = useState(null);
  const [urls, setUrls] = useState({});
  const [uploading, setUploading] = useState(false);
  const [angle, setAngle] = useState('front');
  const [tips, setTips] = useState(false);
  const [before, setBefore] = useState('');
  const [after, setAfter] = useState('');
  const [sharing, setSharing] = useState(false);
  const fileRef = useRef(null);
  const urlsRef = useRef({});

  const fetchBlob = useCallback(async (id) => {
    try {
      const r = await authFetch(`/api/tracking/photo/${id}`);
      if (!r.ok) return;
      const b = await r.blob();
      const u = URL.createObjectURL(b);
      urlsRef.current[id] = u;
      setUrls((m) => ({ ...m, [id]: u }));
    } catch { /* ignore */ }
  }, [authFetch]);

  const load = useCallback(async () => {
    try {
      const d = await api('/api/tracking/photos');
      const list = d.photos || [];
      setPhotos(list);
      list.forEach((p) => { if (!urlsRef.current[p.photo_id]) fetchBlob(p.photo_id); });
      if (list.length) {
        setBefore((b) => b || list[0].photo_id);
        setAfter((a) => a || list[list.length - 1].photo_id);
      }
    } catch { setPhotos([]); }
  }, [api, fetchBlob]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { Object.values(urlsRef.current).forEach((u) => URL.revokeObjectURL(u)); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const b64 = await fileToResizedBase64(file);
      await api('/api/tracking/photos', {
        method: 'POST',
        body: JSON.stringify({ image_base64: b64, date: new Date().toLocaleDateString('en-CA'), angle }),
      });
      toast.success(en ? 'Photo added' : 'Photo ajoutée');
      load();
    } catch {
      toast.error(en ? 'Upload failed' : "Échec de l'envoi");
    } finally { setUploading(false); }
  };

  const del = async (id) => {
    try {
      await api(`/api/tracking/photos/${id}`, { method: 'DELETE' });
      if (urlsRef.current[id]) { URL.revokeObjectURL(urlsRef.current[id]); delete urlsRef.current[id]; }
      setBefore((b) => (b === id ? '' : b));
      setAfter((a) => (a === id ? '' : a));
      load();
    } catch { toast.error(en ? 'Delete failed' : 'Échec de la suppression'); }
  };

  const share = async () => {
    const bP = photos.find((p) => p.photo_id === before);
    const aP = photos.find((p) => p.photo_id === after);
    const bU = bP && urls[bP.photo_id];
    const aU = aP && urls[aP.photo_id];
    if (!bU || !aU) { toast.error(en ? 'Photos still loading' : 'Photos en cours de chargement'); return; }
    setSharing(true);
    try {
      const wd = (bP.weight != null && aP.weight != null) ? +(aP.weight - bP.weight).toFixed(1) : null;
      const blob = await buildTransformationImage({
        beforeUrl: bU, afterUrl: aU, beforeDate: bP.date, afterDate: aP.date, wDelta: wd, en,
      });
      const file = new File([blob], 'body-up-transformation.jpg', { type: 'image/jpeg' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Body-Up',
          text: en ? 'My transformation with Body-Up Personal Training 💪' : 'Ma transformation avec Body-Up Personal Training 💪',
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'body-up-transformation.jpg'; a.click();
        URL.revokeObjectURL(url);
        toast.success(en ? 'Image downloaded' : 'Image téléchargée');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(en ? 'Could not generate the image' : "Impossible de générer l'image");
    } finally { setSharing(false); }
  };

  if (photos === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const byId = (id) => photos.find((p) => p.photo_id === id);
  const bP = byId(before);
  const aP = byId(after);
  const wDelta = (bP?.weight != null && aP?.weight != null) ? +(aP.weight - bP.weight).toFixed(1) : null;
  const optLabel = (p) => `${p.date} · ${angleLabel(p.angle, en)}${p.weight != null ? ` · ${p.weight}kg` : ''}`;
  // Rappel visuel doux : encourage la photo régulière (même angle, même éclairage) avec
  // un seuil ADAPTATIF calculé sur la cadence habituelle du client (photos toutes les 2
  // semaines ? tous les mois ? on adapte pour ne pas être trop insistant ni trop laxiste).
  const lastPhotoDate = photos.length ? photos[photos.length - 1].date : null;
  const daysSincePhoto = lastPhotoDate
    ? Math.floor((new Date() - new Date(`${lastPhotoDate}T00:00:00`)) / 86400000)
    : null;
  let photoMedianInterval = null;
  if (photos.length >= 2) {
    const gaps = [];
    for (let i = 1; i < photos.length; i++) {
      const g = Math.round(
        (new Date(`${photos[i].date}T00:00:00`) - new Date(`${photos[i - 1].date}T00:00:00`)) / 86400000
      );
      if (g > 0) gaps.push(g);
    }
    if (gaps.length) {
      gaps.sort((a, b) => a - b);
      const mid = Math.floor(gaps.length / 2);
      photoMedianInterval = gaps.length % 2 === 0 ? Math.round((gaps[mid - 1] + gaps[mid]) / 2) : gaps[mid];
    }
  }
  const photoReminderThreshold = photoMedianInterval
    ? Math.min(60, Math.max(20, Math.round(photoMedianInterval * 1.3)))
    : 25;
  const showPhotoReminder = photos.length > 0 && daysSincePhoto != null && daysSincePhoto >= photoReminderThreshold;

  return (
    <div id="progress-photos-section" className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-5" data-testid="progress-photos">
      <div className="flex items-center justify-between mb-4">
        <h3 className="flex items-center gap-2 font-bold text-white"><Camera className="w-4 h-4 text-primary" /> {en ? 'Before / After photos' : 'Photos avant / après'}</h3>
        <button onClick={() => setTips((t) => !t)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-zinc-300 border border-white/10 hover:border-primary/50 hover:text-white transition-colors" data-testid="photo-tips-btn">
          <Info className="w-3.5 h-3.5" /> {en ? 'Photo tips' : 'Conseils photo'}
        </button>
      </div>

      {showPhotoReminder && (
        <button
          onClick={() => fileRef.current?.click()}
          className="w-full mb-4 rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-primary/10 px-4 py-3 flex items-center justify-between gap-3 hover:border-amber-500/50 transition-colors text-left"
          data-testid="photo-monthly-reminder"
        >
          <div className="flex items-center gap-2.5">
            <Clock className="w-4 h-4 text-amber-400 shrink-0" />
            <p className="text-sm text-white font-semibold">
              {en
                ? "It's photo time! Same angle, same lighting, same framing as last time for an accurate comparison."
                : "C'est le moment de votre photo du mois ! Même angle, même éclairage, même cadrage que la dernière fois pour bien comparer."}
            </p>
          </div>
          <span className="text-xs font-bold text-black bg-gradient-to-r from-amber-400 to-primary px-3 py-1.5 rounded-full whitespace-nowrap shrink-0">{en ? 'Add' : 'Ajouter'}</span>
        </button>
      )}

      <AnimatePresence>
        {tips && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-4" data-testid="photo-tips">
            <div className="rounded-xl bg-primary/10 border border-primary/25 p-4 space-y-1.5">
              {(en ? TIPS.en : TIPS.fr).map((t, i) => (
                <p key={i} className="text-sm text-zinc-300 flex items-start gap-2"><span className="text-primary mt-0.5">•</span>{t}</p>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload */}
      <div className="rounded-xl bg-zinc-800/50 border border-white/10 p-4 mb-5">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-zinc-400">{en ? 'Angle:' : 'Angle :'}</span>
          {ANGLES.map((a) => (
            <button key={a.key} onClick={() => setAngle(a.key)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${angle === a.key ? 'bg-gradient-to-r from-primary to-pink-500 text-white' : 'bg-zinc-900 text-zinc-400 border border-white/10'}`}
              data-testid={`angle-${a.key}`}>
              {en ? a.en : a.fr}
            </button>
          ))}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="w-full py-3 rounded-xl border-2 border-dashed border-white/15 hover:border-primary/50 text-zinc-300 hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="photo-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? (en ? 'Uploading…' : 'Envoi…') : (en ? 'Add a photo' : 'Ajouter une photo')}
        </button>
      </div>

      {/* Compare */}
      {photos.length >= 2 && (
        <div className="mb-5" data-testid="photo-compare">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold tracking-wider text-primary flex items-center gap-1.5"><ArrowLeftRight className="w-3.5 h-3.5" /> {en ? 'COMPARE' : 'COMPARER'}</p>
            <button onClick={share} disabled={sharing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-black bg-gradient-to-r from-yellow-400 to-primary hover:scale-105 transition-transform disabled:opacity-60"
              data-testid="share-transformation-btn">
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5" />}
              {en ? 'Share my transformation' : 'Partager ma transformation'}
            </button>
          </div>

          {/* Comparateur avant/après avec curseur glissant */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-zinc-900 to-black p-2">
            <BeforeAfterSlider
              beforeUrl={bP && urls[bP.photo_id]}
              afterUrl={aP && urls[aP.photo_id]}
              beforeLabel={en ? 'BEFORE' : 'AVANT'}
              afterLabel={en ? 'AFTER' : 'APRÈS'}
              beforeMeta={bP ? `${bP.date}${bP.weight != null ? ` · ${bP.weight} kg` : ''}` : null}
              afterMeta={aP ? `${aP.date}${aP.weight != null ? ` · ${aP.weight} kg` : ''}` : null}
              en={en}
            />
            {/* Sélecteurs */}
            <div className="grid grid-cols-2 gap-px bg-white/10 mt-2 rounded-lg overflow-hidden">
              {[{ v: before, set: setBefore, t: 'before' }, { v: after, set: setAfter, t: 'after' }].map((col) => (
                <div key={col.t} className="relative bg-zinc-900">
                  <select value={col.v} onChange={(e) => col.set(e.target.value)}
                    className="w-full appearance-none bg-transparent pl-3 pr-8 py-2.5 text-xs text-white focus:outline-none"
                    data-testid={`compare-select-${col.t}`}>
                    {photos.map((p) => <option key={p.photo_id} value={p.photo_id}>{optLabel(p)}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 text-zinc-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              ))}
            </div>
          </div>

          {wDelta != null && (
            <div className="mt-3 text-center">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${wDelta < 0 ? 'bg-emerald-500/15 text-emerald-400' : wDelta > 0 ? 'bg-pink-500/15 text-pink-400' : 'bg-white/10 text-zinc-300'}`} data-testid="compare-weight-delta">
                {wDelta > 0 ? '+' : ''}{wDelta} kg
              </span>
            </div>
          )}
        </div>
      )}

      {/* Gallery */}
      {photos.length === 0 ? (
        <p className="text-center text-zinc-500 text-sm py-6">{en ? 'No photos yet. Add your first one to start your transformation!' : 'Aucune photo. Ajoutez la première pour démarrer votre transformation !'}</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="photo-gallery">
          {photos.map((p) => (
            <div key={p.photo_id} className="relative group aspect-[3/4] rounded-lg overflow-hidden bg-zinc-800 border border-white/10" data-testid="photo-thumb">
              {urls[p.photo_id]
                ? <img src={urls[p.photo_id]} alt={p.date} className="w-full h-full object-cover object-[50%_18%]" />
                : <div className="w-full h-full flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-zinc-600" /></div>}
              <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-[9px] font-bold text-white">{angleLabel(p.angle, en)}</span>
              <span className="absolute bottom-1 left-1 right-1 text-[9px] text-white/90 truncate bg-black/40 px-1 rounded">{p.date}</span>
              <button onClick={() => del(p.photo_id)} className="absolute top-1 right-1 p-1 rounded-md bg-black/60 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="photo-delete">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgressPhotos;
